import "./workOn.css";
import { Helmet } from "react-helmet";
import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const stats = [
  { number: "20+", title: "Connected To Countries", icon: "./img/1.png" },
  { number: "120+", title: "Connected To Clients", icon: "./img/group.png" },
  { number: "500+", title: "Projects Delivered", icon: "./img/vector.png" },
  { number: "30+", title: "Technologies We Work", icon: "./img/chip.png" },
  { number: "25+", title: "Average CTA", icon: "./img/metrize.png" },
  { number: "5+", title: "Exploring New Avenues", icon: "./img/launch.png" },
];

// FIXED COUNTER COMPONENT - Works on all devices
function Counter({ value }) {
  const [count, setCount] = useState(0);
  const counterRef = useRef(null);
  const hasAnimated = useRef(false);
  const [hasStarted, setHasStarted] = useState(false);

  // Parse the number from value (remove + sign)
  const targetNumber = parseInt(value.replace("+", ""));

  useEffect(() => {
    // Setup intersection observer to start counting when visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            setHasStarted(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 } // Trigger when even 10% is visible
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime = null;
    let animationFrame = null;
    const duration = 2000; // 2 seconds animation

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / duration);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(easeOutQuart * targetNumber);
      
      setCount(currentValue);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [hasStarted, targetNumber]);

  return <span ref={counterRef}>{count}+</span>;
}

export default function WorkOn() {
  const services = [
    {
      title: "Smart Contracts",
      desc: "550+ smart contracts deployed — driving real innovation across multiple blockchain ecosystems.",
      icon: "./img/Animation - 1714160338354.gif",
      count: "550+",
      label: "Contracts Deployed"
    },
    {
      title: "ETH RPC & Validator Nodes",
      desc: "9+ Ethereum-compatible chains with robust RPC endpoints and validator infrastructure.",
      icon: "./img/Animation - 1714158417783.gif",
      count: "9+",
      label: "Chains Supported"
    },
    {
      title: "Cross-Chain Solutions",
      desc: "13+ battle-tested cross-chain bridges enabling secure and efficient asset movement.",
      icon: "./img/Animation - 1714159442239.gif",
      count: "13+",
      label: "Bridges Built"
    },
    {
      title: "Wallet Development",
      desc: "3 production-grade wallets built with security-first architecture and smooth UX.",
      icon: "./img/Animation - 1714164352081.gif",
      count: "3",
      label: "Wallets Created"
    },
    {
      title: "AUDITING & SECURITY",
      desc: "Smart-contract & Blockchain audits, ensuring the security and integrity of blockchain systems",
      icon: "./img/Animation - 1714199891999.gif",
      count: "100%",
      label: "Security Guarantee"
    },
    {
      title: "DATA MIGRATION",
      desc: "Migrated data 21+ ensuring seamless transition and continuity in blockchain operations.",
      icon: "./img/Animation - 1714221256745.gif",
      count: "21+",
      label: "Data Migrations"
    },
    {
      title: "INTERNET OF THINGS",
      desc: "Over 10+ IoT devices deployed, revolutionizing connectivity and data-driven insights",
      icon: "./img/Animation - 1714223375639.gif",
      count: "10+",
      label: "IoT Devices"
    },
    {
      title: "MOBILE APPLICATION",
      desc: "Over 50+ empowering users with innovative solutions and seamless experiences.",
      icon: "./img/Animation - 1714223768546.gif",
      count: "50+",
      label: "Mobile Apps"
    },
    {
      title: "Website Development",
      desc: "We have successfully delivered high-performance web platforms including .",
      icon: "./img/Animation - 1714221991145.gif",
      count: "100+",
      label: "Websites"
    },
    {
      title: "MAJOR PLATFORM",
      desc: "Exchanges : 2+ NFT MarketPlace : 3+ Social Apps : 5+ Ecommerce WebApp : 5+",
      icon: "./img/Animation - 1714158417783.gif",
      count: "15+",
      label: "Platforms Built"
    },
  ];

  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Premium left side animations - subtle and elegant
  const leftOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [1, 0.95, 0.85, 0.75]);
  const leftBlur = useTransform(scrollYProgress, [0, 0.5, 1], [0, 2, 4]);
  const leftY = useTransform(scrollYProgress, [0, 0.5, 1], [0, -8, -15]);

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4 },
    },
  };

  // Premium card design variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1]
      }
    })
  };

  return (
    <div className="">
    
    <Helmet>
        <meta charset="UTF-8" />
        <link rel="icon" href="/img/CML_LOGO.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <meta
          name="description"
          content="Chain Magician Lab - Your one-stop solution for all software requirements. We specialize in blockchain development, including Ethereum development and smart contracts. Our mobile application services cover e-commerce apps, gaming apps, Web3 apps, and more. We also provide custom software solutions tailored to your business needs, along with decentralized application development and secure, scalable software solutions."
        />
        <title>
          Chain Magician Lab | Blockchain, Mobile & Custom Software Development
        </title>
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.chainmagicianlab.com/" />
        <meta
          property="og:title"
          content="Chain Magician Lab | Blockchain, Mobile & Custom Software Development"
        />
        <meta
          property="og:description"
          content="Chain Magician Lab offers high-quality blockchain development, mobile app creation including e-commerce, gaming, and Web3 apps, and custom software solutions to meet your business needs."
        />
        <meta
          property="og:image"
          content="https://www.chainmagicianlab.com/img/CML_LOGO.png"
        />
        <meta property="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content="https://www.chainmagicianlab.com/"
        />
        <meta
          property="twitter:title"
          content="Chain Magician Lab | Blockchain, Mobile & Custom Software Development"
        />
        <meta
          property="twitter:description"
          content="Chain Magician Lab provides comprehensive software solutions including blockchain development, mobile apps (e-commerce, gaming, Web3), custom software, decentralized applications, and smart contracts."
        />
        <meta
          property="twitter:image:alt"
          content="https://www.chainmagicianlab.com/img/CML_LOGO.png"
        />
        <link rel="canonical" href="https://www.chainmagicianlab.com/" />
        <meta
          name="keywords"
          content="Blockchain development, Blockchain development company in India, Best blockchain company, Ethereum development services, Smart contract development, Smart contract developers India, dApp development company, Decentralized application development, Crypto exchange development India, Centralized exchange (CEX) development, Decentralized exchange (DEX) development, DEX aggregator platform, DEX liquidity pools, DEX security audits, DEX smart contracts, Cross-chain DEX development, Crypto arbitrage trading, Yield farming platform development, DeFi development company, DeFi staking platforms, DeFi lending protocols, DeFi token development, Cryptocurrency development, Crypto coin development, Crypto wallet development, Mobile crypto wallets, Web crypto wallets, Custodial and non-custodial wallets, ICO development company, STO development company, Token development services, Top token development company India, ERC-20 token development, BEP-20 token development, NFT token development, NFT marketplace development India, NFT smart contract solutions, NFT game development, Play-to-earn game development, NFT launchpad development, Tokenomics consulting, Token audit services, Token liquidity management, Token sale strategy, Token staking platform, Token governance models, Web3 integration company, Web3 application development, Web3 wallets, Web3 projects, Web3 APIs, Web3 infrastructure solutions, Web3 scalability, Web3 consulting India, Decentralized web solutions, DAO development services, Metaverse development company, Blockchain in healthcare India, Blockchain in supply chain India, Blockchain in fintech India, Blockchain consulting services, Blockchain architecture design, Blockchain protocol development, Blockchain auditing company, Blockchain security testing, Private blockchain development, Hyperledger development, Enterprise blockchain solutions, Blockchain IoT integration, AI and blockchain solutions, MLM software development company India, Best MLM software company, MLM compensation plans, Binary MLM plan software, Matrix MLM plan development, Unilevel MLM plan, Hybrid MLM software, Forced matrix MLM, MLM income calculator India, MLM business model solutions, MLM software features, MLM strategy consulting, MLM apps development, MLM software with blockchain, MLM company reviews India, Custom MLM software solutions, Mobile app development services, iOS app development India, Android app development company, Cross-platform mobile apps, Native app development services, Enterprise mobile app development, Mobile app UI/UX design, App store optimization, Mobile app testing services, Mobile app marketing, Mobile app maintenance, App deployment services, Custom mobile application solutions, Custom software development, Software development company in India, Enterprise software development, SaaS development company, PaaS development, Cloud software solutions, Software consulting India, Software modernization services, Bespoke software development, Software integration solutions, E-commerce website development India, Shopify development company India, Magento development, WooCommerce development, Custom e-commerce app development, Multi-vendor marketplace India, Online store development, E-commerce strategy consulting, E-commerce SEO services, Gaming app development company, Unity game development India, Unreal Engine game development, VR game development, AR game development, 2D game development, 3D game development, Multiplayer game apps, Educational game apps, Casual game development, Indie game development, Game monetization strategy, Blockchain gaming solutions, Play-to-earn game apps, Web3 gaming platforms, Crypto game development, Best blockchain consulting company India, Top blockchain companies in India, Best token development services, Crypto exchange developers India, Web3 developers India, Mobile app developers India, Custom software company India, Blockchain startup solutions, Token launch services, ICO launch company India.
"
        ></meta>
        <script type="application/ld+json">
          {`
{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Chain Magician Lab",
  "url": "https://www.chainmagicianlab.com",
  "logo": "https://www.chainmagicianlab.com/img/CML_LOGO.png",
  "tagline": "Magic in every line of software.",
  "description": "Chain Magician Lab specializes in blockchain development, Web3 integration, custom software, mobile apps, ecommerce apps, gaming apps, AI, and IoT solutions. We deliver innovative technology with strong expertise and nation-first vision.",
  "founder": {
    "@type": "Person",
    "name": "Anmol",
    "jobTitle": "Founder"
  },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+91-6393561379",
      "contactType": "customer support",
      "areaServed": "IN",
      "availableLanguage": ["English", "Hindi"]
    },
    {
      "@type": "ContactPoint",
      "email": "chainmagicianlab@gmail.com",
      "contactType": "sales",
      "areaServed": "IN"
    }
  ],
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "India"
  },
  "sameAs": [
    "https://www.linkedin.com/company/chain-magician-lab",
    "https://twitter.com/chainmagicianlab",
    "https://t.me/decryptcoders"
  ],
  "makesOffer": [
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Blockchain Development",
        "description": "End-to-end blockchain solutions, custom smart contracts, and decentralized applications."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Web3 Integration",
        "description": "Seamless wallet integration, dApps, NFT marketplaces, and DeFi solutions."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Custom Software Development",
        "description": "Bespoke software tailored for businesses across industries."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Mobile Application Development",
        "description": "iOS, Android, Flutter, and React Native apps with modern UI/UX."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Ecommerce App Development",
        "description": "Secure and scalable ecommerce applications with payment integration."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Gaming Application Development",
        "description": "High-performance game development with Web3 and traditional support."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "AI & Machine Learning Solutions",
        "description": "AI-driven software, predictive analytics, and intelligent automation."
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "IoT Solutions",
        "description": "Smart IoT development with focus on industrial and defense applications."
      }
    }
  ]
}
`}
        </script>
      </Helmet>
      {/* EXPERTISE SECTION - PREMIUM CLASSIC DESIGN */}
      <section 
        ref={containerRef}
        className="relative bg-white"
        style={{ minHeight: "100vh" }}
      >
  

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-24 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
            
            {/* LEFT SIDE - PREMIUM STICKY DESIGN */}
            <div className="lg:col-span-5">
              <motion.div 
                className="lg:sticky lg:top-32"
                style={{
                  opacity: leftOpacity,
                  filter: `blur(${leftBlur}px)`,
                  y: leftY
                }}
              >
                {/* Premium Divider Line */}
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: 60 }}
                  transition={{ duration: 0.8 }}
                  className="w-[60px] h-[2px] bg-[#12AF09] mb-8"
                />

                {/* Elegant Badge */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6"
                >
                  <span className="text-xs tracking-[0.2em] uppercase text-gray-400 font-light">
                    Our Expertise
                  </span>
                </motion.div>

                {/* Main Title */}
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-5xl md:text-6xl font-light leading-[1.2] mb-6"
                >
                  <span className="text-gray-900">What We</span>
                  <br />
                  <span className="text-[#12AF09] font-medium">Excel At</span>
                </motion.h2>

                {/* Description */}
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-gray-700 text-base leading-relaxed mb-10"
                >
                  Delivering cutting-edge blockchain solutions with proven expertise across multiple domains
                </motion.p>

                {/* Premium Stats */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="space-y-6 mb-12"
                >
                  <div className="flex items-center justify-between py-4 border-t border-gray-100">
                    <span className="text-gray-600 text-sm">Years of Excellence</span>
                    <span className="text-2xl font-light text-gray-900">3+</span>
                  </div>
                  <div className="flex items-center justify-between py-4 border-t border-gray-100">
                    <span className="text-gray-600 text-sm">Projects Completed</span>
                    <span className="text-2xl font-light text-gray-900">500+</span>
                  </div>
                  <div className="flex items-center justify-between py-4 border-t border-gray-100 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">Global Clients</span>
                    <span className="text-2xl font-light text-gray-900">120+</span>
                  </div>
                </motion.div>

            

                {/* Minimal Progress Indicator */}
                <motion.div 
                  className="hidden lg:block mt-16"
                  style={{ opacity: leftOpacity }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-[1px] bg-gray-200">
                      <motion.div 
                        className="h-full bg-[#12AF09]"
                        style={{ scaleX: scrollYProgress, transformOrigin: "0%" }}
                      />
                    </div>
                    <motion.span 
                      className="text-[10px] font-mono text-gray-400"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {Math.round(scrollYProgress.get() * 100)}%
                    </motion.span>
                  </div>
                  <p className="text-[10px] text-gray-300 mt-2 tracking-wide">
                    SCROLL TO EXPLORE
                  </p>
                </motion.div>
              </motion.div>
            </div>

            {/* RIGHT SIDE - PREMIUM MINIMAL CARDS */}
            <div className="lg:col-span-7">
              <div className="space-y-12">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    className="group"
                  >
                    <div className="relative border-l-2 border-gray-100 pl-8 md:pl-10 transition-all duration-500 hover:border-l-[#12AF09]">
                      {/* Card Number */}
                      <div className="absolute -left-5 top-0 w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center text-xs text-gray-400 font-mono group-hover:border-[#12AF09] group-hover:text-[#12AF09] transition-all duration-300">
                        {String(index + 1).padStart(2, '0')}
                      </div>

                      {/* Card Content */}
                      <div className="py-6">
                        <div className="flex items-start gap-6">
                          {/* Icon */}
                          <div className="flex-shrink-0 w-16 h-16 rounded-lg  flex items-center justify-center group-hover:bg-gray-100 transition-all duration-300">
                            <img
                              src={service.icon}
                              alt={service.title}
                              className="w-15 h-15 object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                            />
                          </div>

                          {/* Text Content */}
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4 mb-3">
                              <h3 className="text-xl font-medium text-gray-900 group-hover:text-[#12AF09] transition-colors">
                                {service.title}
                              </h3>
                              <div className="text-right">
                                <div className="text-2xl font-light text-gray-900">{service.count}</div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-400">{service.label}</div>
                              </div>
                            </div>
                            <p className="text-gray-800 text-sm leading-relaxed mb-4">
                              {service.desc}
                            </p>
                            
                            {/* Minimal CTA */}
                            <motion.div 
                              className="inline-flex items-center gap-2 cursor-pointer group/cta"
                              whileHover={{ x: 5 }}
                            >
                              <span className="text-xs font-medium text-gray-400 group-hover/cta:text-[#12AF09] transition-colors">
                                Discover more
                              </span>
                              <svg className="w-3 h-3 text-gray-300 group-hover/cta:text-[#12AF09] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                              </svg>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* Elegant End Indicator */}
                <motion.div 
                  className="text-center pt-8"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  <div className="inline-flex items-center gap-4">
                    <div className="w-8 h-[1px] bg-gray-200"></div>
                    <span className="text-[10px] uppercase tracking-wider text-gray-300 font-light">
                      Complete Collection
                    </span>
                    <div className="w-8 h-[1px] bg-gray-200"></div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUTTON SECTION */}
      <div className="setbody relative z-10">
        <button className="mx-2">Get Quotation</button>
        <button>Get Exact Pricing</button>
      </div>

      {/* STATS SECTION - PREMIUM REDESIGN */}
      <section
        className="relative py-20 md:py-32 px-4 lg:px-8 bg-white"
      >
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 60 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-[60px] h-[2px] bg-[#12AF09] mx-auto mb-6"
            />
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-900 mb-4 px-2">
              Connected World,
              <br />
              <span className="text-[#12AF09] font-medium">Connected Experience</span>
            </h2>

            <p className="text-sm md:text-base text-gray-400 max-w-2xl mx-auto px-4">
              Delivering excellence across borders with passion and precision
            </p>
          </motion.div>

          {/* Premium Stats Grid */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6"
          >
            {stats.map((item, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -4 }}
                className="group relative bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-3 sm:p-4 md:p-6 shadow-lg shadow-gray-100/50 hover:shadow-xl hover:shadow-[#12AF09]/10 transition-all duration-500"
              >
                {/* Hover Gradient */}
                <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#12AF09]/0 via-[#12AF09]/0 to-[#12AF09]/0 group-hover:from-[#12AF09]/5 group-hover:to-[#1b8a10]/5 transition-all duration-500"></div>

                {/* Top Accent Line */}
                <motion.div
                  initial={{ width: 0 }}
                  whileHover={{ width: "4rem" }}
                  transition={{ duration: 0.3 }}
                  className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 sm:h-1 bg-gradient-to-r from-[#12AF09] to-[#1b8a10] rounded-full"
                />

                {/* Icon Container */}
                <div className="relative mb-2 sm:mb-3 md:mb-4">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                    className="w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20 mx-auto bg-[#12AF09]/5 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center group-hover:bg-[#27bd1927] transition-all duration-300"
                  >
                    <img
                      src={item.icon}
                      alt={item.title}
                      className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain transition-all duration-300"
                    />
                  </motion.div>

                  {/* Small Decorative Circle */}
                  <motion.div
                    whileHover={{ scale: 1.2, borderColor: "#12AF09" }}
                    className="hidden sm:block absolute -top-2 -right-2 w-6 md:w-8 h-6 md:h-8 border-2 border-[#12AF09]/10 rounded-full group-hover:border-[#12AF09]/30 transition-colors duration-300"
                  />
                </div>

                {/* Counter */}
                <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold text-center mb-1 md:mb-2 text-gray-900">
                  <Counter value={item.number} />
                </h3>

                {/* Title */}
                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 font-medium text-center leading-tight px-1">
                  {item.title}
                </p>

                {/* Bottom Decorative Line */}
                <motion.div
                  initial={{ width: "3rem", opacity: 0.3 }}
                  whileHover={{ width: "5rem", opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="h-0.5 sm:h-1 mx-auto mt-3 sm:mt-4 md:mt-5 rounded-full bg-gradient-to-r from-[#12AF09] to-[#1b8a10]"
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 pt-8 border-t border-gray-100"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
              {[
                { number: "24/7", label: "Support" },
                { number: "100%", label: "Satisfaction" },
                { number: "30+", label: "Experts" },
                { number: "5★", label: "Rating" },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -2 }}
                  className="text-center"
                >
                  <div className="text-lg md:text-xl font-light text-gray-900">
                    {item.number}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">
                    {item.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-12 text-center"
          >
            <p className="text-[10px] uppercase tracking-wider text-gray-300">
              Trusted by companies worldwide
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}